/**
 * customer_job_type_rates — gap 1 / DEC-383 (migration 0271). What a hauler charges one
 * customer for one job type, per the job type's unit ($/bbl, $/hour or $/load). See the
 * migration header for the rules; this mirror keeps schema-diff tooling aligned.
 *
 * Append-only (SELECT+INSERT for app_user). Readers that pick the latest row MUST order by
 * effective_from DESC, revision DESC.
 *
 * - Keyed on job_types.family_id (no FK: family_id is not unique in job_types), so a price
 *   survives version edits. billing_method is the unit the price was set in and is part of
 *   the key, so a job type whose method changes never reads an old-unit price.
 * - Dated, no end dates. A correction of a row dated today or later is a new row for the
 *   same key and day with revision = the highest + 1; the highest revision wins.
 * - Whole cents; 0 is a real price (bills $0.00), never "missing".
 * - EXPRESS tenants also get the RESTRICTIVE customer_job_type_rates_express_visible policy
 *   (0271), which, like the other *_express_visible policies, is not mirrored here.
 */

import { sql } from 'drizzle-orm';
import {
  check,
  date,
  index,
  integer,
  pgPolicy,
  pgTable,
  smallint,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { appUserRole } from './enums.js';
import { haulerOperators } from './platform-catalog.js';
import { tenants, users } from './users.js';

export const customerJobTypeRates = pgTable(
  'customer_job_type_rates',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    haulerOperatorId: uuid('hauler_operator_id')
      .notNull()
      .references(() => haulerOperators.id, { onDelete: 'cascade' }),
    jobTypeFamilyId: uuid('job_type_family_id').notNull(),
    billingMethod: text('billing_method').notNull(),
    rateCents: integer('rate_cents').notNull(),
    effectiveFrom: date('effective_from', { mode: 'string' }).notNull(),
    revision: smallint('revision').notNull().default(0),
    notes: text('notes'),
    importId: uuid('import_id'),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check(
      'customer_job_type_rates_method_check',
      sql`billing_method IN ('PER_BBL', 'HOURLY', 'PER_LOAD')`,
    ),
    check('customer_job_type_rates_revision_check', sql`revision >= 0`),
    check(
      'customer_job_type_rates_rate_range',
      sql`rate_cents >= 0 AND rate_cents <= CASE billing_method WHEN 'PER_BBL' THEN 10000 WHEN 'HOURLY' THEN 100000 ELSE 1000000 END`,
    ),
    uniqueIndex('customer_job_type_rates_day_uq').on(
      t.tenantId,
      t.haulerOperatorId,
      t.jobTypeFamilyId,
      t.billingMethod,
      t.effectiveFrom,
      t.revision,
    ),
    index('customer_job_type_rates_family_idx').on(t.tenantId, t.jobTypeFamilyId),
    pgPolicy('customer_job_type_rates_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type CustomerJobTypeRate = typeof customerJobTypeRates.$inferSelect;
